import { useMutation } from "@tanstack/react-query";
import { createReceiptForBusiness } from "apis";
import getB from "../../utils/bridges/EkaBridge";
import queryClient from "../../instances/react-query-client";
import { getAllQueuesQuery } from "./get-all-queues";
import { RECEIPT_TYPE, TBusiness, TCreateReceiptBody } from "@elixir/types";
import { useMassageReceiptDataWithConfigForPDF } from "./receipt-v2/use-massage-receipt-data-and-config-for-pdf";
import { makeReceiptOnLocal } from "./receipt-v2/should-receipt-be-made-on-local-util";
import { track } from "../../utils/tracking";
import { getPatientDetails } from "../../utils/bridges/methods";

const usePostCreateReceipt = () => {
  const massageReceiptDataWithConfigForPDF =
    useMassageReceiptDataWithConfigForPDF();

  return useMutation(
    async ({
      businessId,
      doctor_oid,
      payload,
      meta,
      isFromQuickReceipt,
      abhaId,
    }: {
      businessId: TBusiness["business_id"];
      doctor_oid?: string;
      payload: TCreateReceiptBody["payload"];
      meta: TCreateReceiptBody["meta"];
      isFromQuickReceipt?: boolean;
      abhaId?: string;
    }) => {
      const shouldGeneratePDF = makeReceiptOnLocal();
      
      // Get abhaId from bridge if not provided
      let finalAbhaId = abhaId;
      if (!finalAbhaId && meta.patient_oid) {
        const patients = await getPatientDetails({ patientOids: [meta.patient_oid] });
        finalAbhaId = patients?.[0]?.patient?.abha?.id;
      }
      
      return createReceiptForBusiness({
        businessId,
        doctor_oid,
        payload,
        meta,
        isFromQuickReceipt,
        shouldGeneratePDF,
        abhaId: finalAbhaId,
      });
    },
    {
      onSuccess: async (
        response,
        { payload, meta, isFromQuickReceipt = false }
      ) => {
        const toMakeReceiptOnLocal = makeReceiptOnLocal();
        if (toMakeReceiptOnLocal) {
          massageReceiptDataWithConfigForPDF(response);
        }

        const bridge = await getB();
        track("CREATE_RECEIPT_V3", {
          isMadeOnLocal: String(toMakeReceiptOnLocal),
          receiptID: String(response?.data?.receipt_id || ""),
          aid: String(response?.data?.aid || ""),
        });

        if (
          payload.amount_due ||
          meta.receipt_type === RECEIPT_TYPE.DUE_PAYMENT
        ) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await bridge.fetchPatientListFromServer([meta.patient_oid]);
          queryClient.invalidateQueries(getAllQueuesQuery({}));
        }
      },
    }
  );
};

export default usePostCreateReceipt;
